/**
 * EditResidentialAddressView — modal sub-view for editing or adding a
 * patient address from the appointment-context home-visit picker.
 *
 * Hosts the same `AddressEditFields` body as the patient-chart inline
 * editor (`address-row.tsx`'s `AddressEditCard`); differs in chrome and
 * save semantics:
 *
 *   - **Chrome**: top header bar with Back / "Add Address" title /
 *     Save action. No inline card border — fills the host panel.
 *   - **Save semantics**: commits immediately via `PUT /api/Patients/{id}`
 *     (via `useUpdatePatient`). The inline patient-chart variant defers
 *     to the tab-level "Save changes" action.
 *
 * Mirrors Flutter `EditResidentialAddressProfileWidget`
 * (`scheduling_module/.../edit_residential_address_profile_widget.dart`).
 *
 * Save flow — verified against Flutter source:
 *   1. Duplicate-locationName check across the patient's address list
 *      (Flutter `:113-125`). Toast `"Location name <name> already exist."`
 *      on collision; abort save.
 *   2. Add vs Edit branching by `appointment.residentialAddress.id`
 *      matching an existing `contactId` (Flutter `:127-160`).
 *   3. isDefault enforcement (Flutter `:162-168`): if the new row is
 *      default, clear isDefault on every other row.
 *   4. At-least-one-default rule (Flutter `:170-181`): if no row is
 *      default after step 3, force the first one default.
 *   5. PUT `/api/Patients/{patientId}` via `useUpdatePatient` (shipped
 *      hook). On 200 success, call `onSave(savedAddress)`; the caller
 *      re-fires PreBookSelectedPatient to refresh the picker.
 *
 * Cross-domain import note (Andrew override 2026-05-20): this lives
 * under `src/patients/` but is imported by `src/scheduling/` (the home-
 * visit picker). The "never cross-import between domains" rule is
 * relaxed for this case because the address editor is genuinely a
 * shared concern between the patient-chart Edit Patient flow and the
 * appointment-context home-visit picker.
 */

import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { V2_CARD_EDGE, V2_PANEL_TITLE, v2Look } from '@/shared/ui/design-v2';
import { toast } from 'sonner';
import { Button } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { useUpdatePatient } from '@/patients/services/use-update-patient';
import { usePatient } from '@/patients/services/use-patient';
import { AddressEditFields, isAddressSaveEnabled } from './address-edit-fields';
import { prepareAddressSave } from './address-save-flow';
import { defaultAddressRow, type AddressFormValues } from './contact-details-schema';
import type { PatientAddress as WirePatientAddress } from '@/types/patient-api';

export interface EditResidentialAddressViewProps {
  patientId: string;
  /**
   * The appointment's currently-selected address id (matches a `contactId`
   * on the patient's address book). When provided AND matched, the view
   * opens in edit mode pre-populated with that row. When null / unmatched,
   * the view opens in add mode with a blank row.
   */
  appointmentSelectedAddressId?: string | null;
  onCancel: () => void;
  /**
   * Called after a successful PUT 200. Receives the saved address row
   * (Shape B). Caller is responsible for triggering picker refresh
   * (typically re-fires PreBookSelectedPatient).
   */
  onSave: (saved: WirePatientAddress) => void;
}

export function EditResidentialAddressView({
  patientId,
  appointmentSelectedAddressId,
  onCancel,
  onSave,
}: EditResidentialAddressViewProps) {
  // PROD-2743 · switch ON: DS `back` glyph, the title on the DS panel-title step in text-primary
  // (not brand ink), the form card with the card border + resting shadow-2. OFF = today.
  const { data: patient, isLoading: isPatientLoading } = usePatient(patientId);
  const updateMutation = useUpdatePatient();

  // Local editable row. Initialised once the patient lands; either the
  // matching existing row converted to AddressFormValues, or a blank row
  // (add mode).
  const [row, setRow] = useState<AddressFormValues | null>(null);
  const isEditMode = useMemo(() => {
    if (!patient || !appointmentSelectedAddressId) return false;
    const list = patient.updatePatientContactRequest?.patientAddress ?? [];
    return list.some(
      (a): a is WirePatientAddress =>
        !!a && a.contactId === appointmentSelectedAddressId,
    );
  }, [patient, appointmentSelectedAddressId]);

  useEffect(() => {
    if (!patient || row) return;
    const list = patient.updatePatientContactRequest?.patientAddress ?? [];
    if (appointmentSelectedAddressId) {
      const match = list.find(
        (a): a is WirePatientAddress =>
          !!a && a.contactId === appointmentSelectedAddressId,
      );
      if (match) {
        setRow(wireToForm(match));
        return;
      }
    }
    setRow(defaultAddressRow());
  }, [patient, appointmentSelectedAddressId, row]);

  const handleSave = () => {
    if (!patient || !row || !isAddressSaveEnabled(row)) return;

    const list = (patient.updatePatientContactRequest?.patientAddress ?? []).filter(
      (a): a is WirePatientAddress => !!a,
    );
    const savedRow = formToWire(row);

    // Steps 1-4 (dup-check / add-vs-edit / isDefault enforcement /
    // at-least-one-default) computed via the pure
    // `prepareAddressSave` helper. Unit-tested in
    // `address-save-flow.test.ts`.
    const outcome = prepareAddressSave(list, savedRow);
    if (!outcome.ok) {
      if (outcome.error === 'duplicate-location-name') {
        toast.error(`Location name ${outcome.conflictName} already exist.`);
      }
      return;
    }

    // Step 5 — PUT /api/Patients/{patientId}.
    const nextPatient = {
      ...patient,
      updatePatientContactRequest: {
        ...(patient.updatePatientContactRequest ?? { patientId }),
        patientAddress: outcome.nextList,
      },
    };
    updateMutation.mutate(
      { patientId, patient: nextPatient },
      {
        onSuccess: () => {
          toast.success('Patient Data Updated Successfully');
          onSave(savedRow);
        },
        // onError: global toast handler reports it; no inline UI.
      },
    );
  };

  const title = isEditMode ? 'Edit Address' : 'Add Address';
  const saveEnabled =
    !!row && isAddressSaveEnabled(row) && !updateMutation.isPending && !isPatientLoading;

  return (
    <div className="flex flex-col h-full bg-surface-card">
      {/* Top header bar — Back / title / Save. Sticky so the form scrolls
          beneath it on tall content. */}
      <div className="flex items-center justify-between px-4 h-14 bg-surface-card border-b border-border-default sticky top-0 z-10">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1 text-sm font-medium text-brand-primary hover:underline"
        >
          <ChevronLeft className="size-4" />
          Back
        </button>
        <p className={v2Look('text-base font-bold text-brand-depth', V2_PANEL_TITLE)}>{title}</p>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={!saveEnabled}
        >
          Save
        </Button>
      </div>

      <div className="flex-1 overflow-auto scrollbar-hide p-4">
        <div className={v2Look('rounded-lg border border-border-default bg-surface-card p-6', V2_CARD_EDGE)}>
          {isPatientLoading || !row ? (
            <div className="space-y-6">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-2/3" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : (
            <AddressEditFields
              row={row}
              onChange={setRow}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Shape mappers ─────────────────────────────────────────────────────────

/** Convert wire (Shape B, 13 fields) → form values (AddressFormValues). */
function wireToForm(a: WirePatientAddress): AddressFormValues {
  // Manual-mode heuristic mirrors Flutter
  // `edit_residential_address_profile_widget.dart:247-250`:
  // if city / country / lat / lng are all blank, this row was entered
  // manually (no place pick) — open the editor in manual mode.
  const isManual =
    isBlank(a.city) && isBlank(a.country) && isBlank(a.latitude) && isBlank(a.longitude);
  return {
    contactId: a.contactId ?? null,
    locationName: a.locationName ?? '',
    building: a.building ?? null,
    apartment: a.apartment ?? null,
    address: a.address ?? null,
    city: a.city ?? null,
    country: a.country ?? null,
    latitude: a.latitude ?? null,
    longitude: a.longitude ?? null,
    notes: a.notes ?? null,
    isDefault: a.isDefault ?? false,
    isActive: a.isActive ?? true,
    isDeleted: a.isDeleted ?? false,
    isEdit: true,
    isManualAddress: isManual,
  };
}

/** Convert form values → wire (Shape B). Drops UI-only flags. */
function formToWire(row: AddressFormValues): WirePatientAddress {
  return {
    contactId: row.contactId ?? null,
    locationName: row.locationName,
    building: row.building,
    apartment: row.apartment,
    notes: row.notes,
    city: row.city,
    country: row.country,
    latitude: row.latitude,
    longitude: row.longitude,
    address: row.address,
    isDeleted: row.isDeleted,
    isDefault: row.isDefault,
    isActive: row.isActive,
  };
}

function isBlank(v: string | null | undefined): boolean {
  return (v ?? '').trim().length === 0;
}
